import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { CERTIFICATE_LINKING_STEPS } from '../../petDemographics.constants';
import { updateCustomerEmail as updateCustomerEmailAction } from '../../../../actions';

const useDateOfHireForm = ({
  certificates = [],
  email = '',
  setCurrentStep = () => { },
  setRegisteredEmail = () => { },
  setCertificateNumber = () => { },
}) => {
  const { t } = useTranslation('petDemos');
  const dispatch = useDispatch();
  const updateCustomerEmailState = useSelector(
    ({ petDemographics }) => petDemographics.updateCustomerEmail,
  );

  const [dateOfHire, setDateOfHire] = useState(null);
  const [dateOfHireError, setDateOfHireError] = useState('');
  const [showError, setShowError] = useState(false);
  // Tracks our own updateCustomerEmail dispatch specifically — see the
  // same guard in useCertificateLinkingScreen.js for why this can't just
  // read updateCustomerEmailState.updated directly (shared redux slice
  // with useAuthorizationCode's OTP flow).
  const [isLinkingEmailDirectly, setIsLinkingEmailDirectly] = useState(false);

  const handleDateChange = (date) => {
    setDateOfHire(date);
    setShowError(false);
    if (date) {
      setDateOfHireError('');
    }
  };

  const handleDateOfHireBlur = () => {
    if (!dateOfHire) {
      setDateOfHireError(
        t('certificateLinking.verifyDateOfHire.requiredField'),
      );
    } else {
      setDateOfHireError('');
    }
  };

  const handleSubmit = () => {
    if (!dateOfHire) {
      setDateOfHireError(
        t('certificateLinking.verifyDateOfHire.requiredField'),
      );
      return;
    }

    const selectedDate = moment(dateOfHire).format('YYYY-MM-DD');

    const matchedCertificate = certificates.find((cert) => (
      cert.dateOfHire && cert.dateOfHire.split('T')[0] === selectedDate
    ));

    if (!matchedCertificate) {
      setShowError(true);
      return;
    }

    if (!matchedCertificate.emailMatched && matchedCertificate.email) {
      setRegisteredEmail(matchedCertificate.email);
      setCertificateNumber(matchedCertificate.certificateNumber);
      setCurrentStep(CERTIFICATE_LINKING_STEPS.SELECT_EMAIL);
    } else if (!matchedCertificate.emailMatched && !matchedCertificate.email) {
      setCertificateNumber(matchedCertificate.certificateNumber);
      setIsLinkingEmailDirectly(true);
      dispatch(updateCustomerEmailAction({
        certificateNumber: matchedCertificate.certificateNumber,
        email,
      }));
    } else {
      setCurrentStep(CERTIFICATE_LINKING_STEPS.EMAIL_LINKED_SUCCESS);
    }
  };

  useEffect(() => {
    if (!isLinkingEmailDirectly) {
      return;
    }

    if (updateCustomerEmailState.loading) {
      return;
    }

    if (updateCustomerEmailState.updated) {
      setIsLinkingEmailDirectly(false);
      setCurrentStep(CERTIFICATE_LINKING_STEPS.EMAIL_LINKED_SUCCESS);
    } else if (updateCustomerEmailState.error) {
      // let the user retry from VERIFY_DATE_OF_HIRE instead of staying stuck
      setIsLinkingEmailDirectly(false);
    }
  }, [
    isLinkingEmailDirectly,
    setCurrentStep,
    updateCustomerEmailState.error,
    updateCustomerEmailState.loading,
    updateCustomerEmailState.updated,
  ]);

  return {
    dateOfHire,
    dateOfHireError,
    handleDateChange,
    handleDateOfHireBlur,
    handleSubmit,
    isLinkingEmailDirectly,
    showError,
  };
};

export { useDateOfHireForm };
